// ─── React & Dependencies ───────────────────────────────────────
import { useCallback } from "react";

// ─── Components ──────────────────────────────────────────────────

// styles
import styles from "./styles.module.scss";

// ─── Hooks & Providers ──────────────────────────────────────────
import { useWallet } from "../../../../providers/wallet/wallet.provider";
import { useNavigate } from "react-router-dom";

import Spinner from "../../../../components/common/Spinner";
import { WalletTransaction } from "../types";
import WalletListItem from "../walletListItem";
import WalletListShimmer from "../walletListShimmer";
import { mapWalletHistory } from "../../../../setup/util/mapWalletHistories";
import { ROUTES } from "../../../../setup/constants";

interface TransactionListProps {
  showMobileView: boolean;
  hideWithdrawTab: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
}

const TransactionList = ({
  showMobileView,
  containerRef,
  hideWithdrawTab,
}: TransactionListProps) => {
  // ─── Hooks & State ────────────────────────────────────────────
  const navigate = useNavigate();

  // ─── Wallet State from Provider ──────────────────────────────
  const transactions = useWallet((state) =>
    hideWithdrawTab ? state.totalTransactions : state.transactions
  );
  const transactionsLoading = useWallet((state) =>
    hideWithdrawTab ? state.totalTransactionsLoading : state.transactionsLoading
  );
  const saveWalletState = useWallet((state) => state.saveWalletState);

  // ─── Callbacks ────────────────────────────────────────────────
  // Save scroll position before navigating
  const saveScrollPosition = useCallback(() => {
    if (containerRef?.current) {
      const scrollPosition = containerRef.current.scrollTop;
      sessionStorage.setItem("walletScrollPosition", scrollPosition.toString());
    }
  }, [containerRef]);

  // Save all wallet state before navigating
  const saveAllStates = useCallback(() => {
    saveScrollPosition();
    saveWalletState();
  }, [saveScrollPosition, saveWalletState]);

  const onContractClick = useCallback(
    (contractId: string): void => {
      saveAllStates();
      navigate(ROUTES.CONTRACT_DETAILS?.replace(":id", contractId));
    },
    [navigate, saveAllStates]
  );

  const handleTransactionClick = useCallback(
    (transaction: WalletTransaction): void => {
      saveAllStates();
      onContractClick(transaction.domainId);
    },
    [onContractClick, saveAllStates]
  );

  // ─── Render Logic ─────────────────────────────────────────────
  // Show loading spinner only on initial load (no transactions yet)
  const showInitialLoading = transactionsLoading && transactions.length === 0;

  // Show empty state when no transactions (error or no data)
  const showEmptyState = transactions.length === 0 && !transactionsLoading;

  // Show load more spinner when loading additional pages
  const showLoadMoreSpinner = transactionsLoading && transactions.length > 0;

  return (
    <>
      {showInitialLoading ? (
        // Initial loading state
        <div className={styles["center_container"]}>
          <WalletListShimmer showMobileView={showMobileView} />
        </div>
      ) : showEmptyState ? (
        // Empty state (no transactions or error occurred)
        <div className={styles["center_container"]}>
          <img
            src="/assets/images/svg/empty_wallet.svg"
            alt="Empty wallet"
            className="empty_wallet_image"
          />
        </div>
      ) : (
        // Transaction list with pagination
        <>
          {/* Render transaction items */}
          {transactions.map((transaction, index) => (
            <WalletListItem
              hideWithdrawTab={hideWithdrawTab}
              milestoneIndex={index} // TODO: Where does this come from?
              key={transaction.domainId || index}
              dash={index > 0}
              transaction={mapWalletHistory(transaction)}
              showMobileView={showMobileView}
              onClick={() =>
                handleTransactionClick(mapWalletHistory(transaction))
              }
            />
          ))}

          {/* Load more spinner */}
          {showLoadMoreSpinner && (
            <div className={styles["center_container--load_more"]}>
              <Spinner />
            </div>
          )}
        </>
      )}
    </>
  );
};

export default TransactionList;
